import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { useForm } from "react-hook-form";
import { api, errorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { RoleBadge, UserStatusBadge } from "@/components/shared/Badges";
import { EmptyState } from "@/components/shared/EmptyState";
import { Skeleton } from "@/components/ui/skeleton";
import { Plus, KeyRound, Ban, Users as UsersIcon, Zap, Gift } from "lucide-react";
import { ROLE_LABEL } from "@/lib/labels";
import { adminGrantFlashCredits, adminGrantLaunchPromo } from "@/lib/promo";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const INTERNAL_ROLES = [
    "SUPER_ADMIN",
    "FRANCHISE_OWNER",
    "DISTRICT_MANAGER",
    "SUPERVISOR",
    "STAFF",
    "ACCOUNTS",
];

export default function AdminUsersPage() {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [role, setRole] = useState("__all__");
    const [status, setStatus] = useState("__all__");
    const [districts, setDistricts] = useState([]);

    const load = async () => {
        setLoading(true);
        const params = new URLSearchParams();
        if (role !== "__all__") params.set("role", role);
        if (status !== "__all__") params.set("status", status);
        try {
            const res = await api.get(`/admin/users?${params.toString()}`);
            setUsers(res.data);
        } catch (e) {
            toast.error(errorMessage(e));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, [role, status]); // eslint-disable-line

    useEffect(() => {
        api.get("/districts").then((r) => setDistricts(r.data)).catch(() => {});
    }, []);

    return (
        <div className="space-y-6" data-testid="admin-users-page">
            <header className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                <div>
                    <div className="text-sm font-medium text-orange-600">Team</div>
                    <h1 className="mt-1 text-3xl font-extrabold text-neutral-900 sm:text-4xl">
                        Users
                    </h1>
                    <p className="mt-1 text-sm text-neutral-500">
                        Create and manage internal team accounts. Shop owners
                        appear here too but are managed via the shop queue.
                    </p>
                </div>
                <CreateUserDialog onCreated={load} districts={districts} />
            </header>

            <div className="grid gap-3 sm:grid-cols-2 md:max-w-md">
                <Select value={role} onValueChange={setRole}>
                    <SelectTrigger data-testid="users-filter-role">
                        <SelectValue placeholder="Role" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="__all__">All roles</SelectItem>
                        {[...INTERNAL_ROLES, "SHOP_OWNER"].map((r) => (
                            <SelectItem key={r} value={r}>
                                {ROLE_LABEL[r]}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <Select value={status} onValueChange={setStatus}>
                    <SelectTrigger data-testid="users-filter-status">
                        <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="__all__">All statuses</SelectItem>
                        <SelectItem value="ACTIVE">Active</SelectItem>
                        <SelectItem value="PENDING_APPROVAL">Awaiting verification</SelectItem>
                        <SelectItem value="REJECTED">Rejected</SelectItem>
                        <SelectItem value="SUSPENDED">Suspended</SelectItem>
                    </SelectContent>
                </Select>
            </div>

            {loading ? (
                <Skeleton className="h-96 rounded-2xl" />
            ) : users.length ? (
                <div className="overflow-x-auto rounded-2xl border border-neutral-200 bg-white">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Name</TableHead>
                                <TableHead>Email</TableHead>
                                <TableHead>Role</TableHead>
                                <TableHead>Status</TableHead>
                                <TableHead>District</TableHead>
                                <TableHead className="text-right">Actions</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {users.map((u) => (
                                <UserRow key={u.id} u={u} districts={districts} onChanged={load} />
                            ))}
                        </TableBody>
                    </Table>
                </div>
            ) : (
                <EmptyState
                    icon={UsersIcon}
                    title="No users match these filters"
                    hint="Try clearing filters or invite your first team member."
                />
            )}
        </div>
    );
}

const CreateUserDialog = ({ onCreated, districts }) => {
    const [open, setOpen] = useState(false);
    const {
        register,
        handleSubmit,
        control,
        reset,
        setValue,
        watch,
        formState: { isSubmitting, errors },
    } = useForm({ defaultValues: { role: "STAFF" } });

    const submit = async (values) => {
        try {
            await api.post("/admin/users", {
                email: values.email.toLowerCase(),
                password: values.password,
                full_name: values.full_name,
                role: values.role,
                district: values.district || null,
            });
            toast.success(`Created user ${values.email}`);
            reset({ role: "STAFF" });
            setOpen(false);
            onCreated?.();
        } catch (e) {
            toast.error(errorMessage(e));
        }
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button className="nest-gradient text-white" data-testid="create-user-btn">
                    <Plus size={16} className="mr-2" /> New user
                </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle>Create internal user</DialogTitle>
                    <DialogDescription>
                        Internal users log in with email + password. Shop owners
                        self-signup with Google.
                    </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSubmit(submit)} className="space-y-3" data-testid="create-user-form">
                    <div>
                        <Label>Full name</Label>
                        <Input {...register("full_name", { required: true })} data-testid="cu-fullname" />
                    </div>
                    <div>
                        <Label>Email</Label>
                        <Input
                            type="email"
                            {...register("email", { required: true })}
                            data-testid="cu-email"
                        />
                    </div>
                    <div>
                        <Label>Password</Label>
                        <Input
                            type="password"
                            {...register("password", { required: true, minLength: 8 })}
                            data-testid="cu-password"
                        />
                        {errors.password && (
                            <p className="mt-1 text-xs text-rose-600">Min 8 characters</p>
                        )}
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label>Role</Label>
                            <Select
                                value={watch("role")}
                                onValueChange={(v) => setValue("role", v)}
                            >
                                <SelectTrigger data-testid="cu-role">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {INTERNAL_ROLES.map((r) => (
                                        <SelectItem key={r} value={r}>
                                            {ROLE_LABEL[r]}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label>District (optional)</Label>
                            <Select
                                value={watch("district") || "__none__"}
                                onValueChange={(v) => setValue("district", v === "__none__" ? "" : v)}
                            >
                                <SelectTrigger data-testid="cu-district">
                                    <SelectValue placeholder="—" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="__none__">None</SelectItem>
                                    {districts.map((d) => (
                                        <SelectItem key={d.slug} value={d.slug}>
                                            {d.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    <DialogFooter className="pt-2">
                        <Button type="submit" disabled={isSubmitting} className="nest-gradient text-white" data-testid="cu-submit">
                            {isSubmitting ? "Creating…" : "Create user"}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
};

const UserRow = ({ u, districts, onChanged }) => {
    const [pwOpen, setPwOpen] = useState(false);
    const [newPw, setNewPw] = useState("");

    const suspend = async () => {
        try {
            await api.delete(`/admin/users/${u.id}`);
            toast.success(`${u.email} suspended`);
            onChanged?.();
        } catch (e) {
            toast.error(errorMessage(e));
        }
    };
    const reactivate = async () => {
        try {
            await api.patch(`/admin/users/${u.id}`, { status: "ACTIVE" });
            toast.success(`${u.email} reactivated`);
            onChanged?.();
        } catch (e) {
            toast.error(errorMessage(e));
        }
    };
    const resetPw = async () => {
        try {
            await api.patch(`/admin/users/${u.id}`, { new_password: newPw });
            toast.success(`Password reset for ${u.email}`);
            setPwOpen(false);
            setNewPw("");
        } catch (e) {
            toast.error(errorMessage(e));
        }
    };

    const district = districts.find((d) => d.slug === u.district)?.name || u.district || "—";

    return (
        <TableRow data-testid={`user-row-${u.email}`}>
            <TableCell className="font-medium">{u.full_name}</TableCell>
            <TableCell className="text-neutral-600">{u.email}</TableCell>
            <TableCell><RoleBadge role={u.role} /></TableCell>
            <TableCell><UserStatusBadge status={u.status} /></TableCell>
            <TableCell className="capitalize">{district}</TableCell>
            <TableCell className="text-right">
                <div className="flex flex-wrap justify-end gap-2">
                    {u.role === "SHOP_OWNER" && u.status === "ACTIVE" && (
                        <>
                            <GrantFlashDialog user={u} onDone={onChanged} />
                            <GrantLaunchPromoBtn user={u} onDone={onChanged} />
                        </>
                    )}
                    {u.password_hash !== null && u.role !== "SHOP_OWNER" && (
                        <Dialog open={pwOpen} onOpenChange={setPwOpen}>
                            <DialogTrigger asChild>
                                <Button variant="outline" size="sm" data-testid={`user-resetpw-${u.email}`}>
                                    <KeyRound size={14} className="mr-1" /> Reset PW
                                </Button>
                            </DialogTrigger>
                            <DialogContent className="sm:max-w-sm">
                                <DialogHeader>
                                    <DialogTitle>Reset password</DialogTitle>
                                    <DialogDescription>
                                        Set a new password for {u.email}.
                                    </DialogDescription>
                                </DialogHeader>
                                <Input
                                    type="password"
                                    value={newPw}
                                    onChange={(e) => setNewPw(e.target.value)}
                                    placeholder="New password (min 8 chars)"
                                    data-testid={`user-newpw-input-${u.email}`}
                                />
                                <DialogFooter>
                                    <Button
                                        disabled={newPw.length < 8}
                                        onClick={resetPw}
                                        className="nest-gradient text-white"
                                        data-testid={`user-newpw-submit-${u.email}`}
                                    >
                                        Save
                                    </Button>
                                </DialogFooter>
                            </DialogContent>
                        </Dialog>
                    )}
                    {u.status === "SUSPENDED" ? (
                        <Button variant="outline" size="sm" onClick={reactivate} data-testid={`user-reactivate-${u.email}`}>
                            Reactivate
                        </Button>
                    ) : (
                        <AlertDialog>
                            <AlertDialogTrigger asChild>
                                <Button variant="outline" size="sm" className="text-rose-600" data-testid={`user-suspend-${u.email}`}>
                                    <Ban size={14} className="mr-1" /> Suspend
                                </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                                <AlertDialogHeader>
                                    <AlertDialogTitle>
                                        Suspend {u.full_name}?
                                    </AlertDialogTitle>
                                    <AlertDialogDescription>
                                        The user will no longer be able to log
                                        in until reactivated.
                                    </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                                    <AlertDialogAction onClick={suspend} className="bg-rose-600 text-white hover:bg-rose-700" data-testid={`user-suspend-confirm-${u.email}`}>
                                        Suspend
                                    </AlertDialogAction>
                                </AlertDialogFooter>
                            </AlertDialogContent>
                        </AlertDialog>
                    )}
                </div>
            </TableCell>
        </TableRow>
    );
};

function GrantFlashDialog({ user, onDone }) {
    const [open, setOpen] = useState(false);
    const [credits, setCredits] = useState(3);
    const [busy, setBusy] = useState(false);
    const submit = async () => {
        try {
            setBusy(true);
            const r = await adminGrantFlashCredits(user.id, Number(credits));
            toast.success(`Granted. Now ${r.remaining} credits remaining.`);
            setOpen(false);
            onDone?.();
        } catch (e) {
            toast.error(errorMessage(e));
        } finally {
            setBusy(false);
        }
    };
    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button variant="outline" size="sm" className="text-amber-700" data-testid={`user-grant-flash-${user.email || user.id}`}>
                    <Zap size={14} className="mr-1" /> Grant credits
                </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-sm">
                <DialogHeader>
                    <DialogTitle>Grant Flash Credits</DialogTitle>
                    <DialogDescription>
                        Add flash boost credits to {user.business_name || user.full_name}.
                    </DialogDescription>
                </DialogHeader>
                <div>
                    <Label>Credits</Label>
                    <Input
                        type="number"
                        min={1}
                        max={100}
                        value={credits}
                        onChange={(e) => setCredits(e.target.value)}
                        data-testid={`grant-flash-input-${user.email || user.id}`}
                    />
                </div>
                <DialogFooter>
                    <Button
                        disabled={busy || !credits || Number(credits) < 1}
                        onClick={submit}
                        className="nest-gradient text-white"
                        data-testid={`grant-flash-submit-${user.email || user.id}`}
                    >
                        {busy ? "Granting…" : "Grant"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function GrantLaunchPromoBtn({ user, onDone }) {
    const [busy, setBusy] = useState(false);
    const grant = async () => {
        try {
            setBusy(true);
            await adminGrantLaunchPromo(user.id, { force: false });
            toast.success("Launch Promo granted.");
            onDone?.();
        } catch (e) {
            toast.error(errorMessage(e));
        } finally {
            setBusy(false);
        }
    };
    return (
        <AlertDialog>
            <AlertDialogTrigger asChild>
                <Button variant="outline" size="sm" className="text-orange-700" data-testid={`user-grant-promo-${user.email || user.id}`} disabled={busy}>
                    <Gift size={14} className="mr-1" /> Grant promo
                </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
                <AlertDialogHeader>
                    <AlertDialogTitle>Grant Launch Promo?</AlertDialogTitle>
                    <AlertDialogDescription>
                        This will activate a free 30-day Gold trial for{" "}
                        <b>{user.business_name || user.full_name}</b>. Any
                        current ACTIVE subscription will be superseded. Cannot be undone.
                    </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                        onClick={grant}
                        className="nest-gradient text-white"
                        data-testid={`grant-promo-confirm-${user.email || user.id}`}
                    >
                        Grant
                    </AlertDialogAction>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    );
}
